'use client';

import { useEffect, useState } from 'react';
import { Dialog } from '@/components/ui/Dialog';
import { useLibraryStore, isPrimaryLibrary } from '@/stores/library-store';
import type { LibraryConnection } from '@/lib/opds/types';

/**
 * Username/password editor for an existing library. Used for two flows:
 *
 *  1. Setting credentials on the primary library when none are configured
 *     yet (initial onboarding after the migration reset, or a fresh
 *     install on a new device).
 *  2. Editing credentials when the user wants to switch users or update
 *     a rotated password.
 *
 * For the primary library the URL is intentionally not editable — it's
 * a build-time constant. For other libraries, use AddLibraryDialog
 * (the URL is part of their identity and gets set at creation).
 */
export function LibraryAuthDialog({
  library,
  open,
  onClose,
  onSaved,
}: {
  library: LibraryConnection | null;
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const updateLibrary = useLibraryStore((s) => s.updateLibrary);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [testing, setTesting] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);

  // Pre-fill the username from the existing record so a user updating
  // a rotated password doesn't have to retype their name. Password is
  // never pre-filled — Basic-auth credentials live in IndexedDB but
  // surfacing them in plaintext would be a regression.
  useEffect(() => {
    if (open && library) {
      setUsername(library.auth?.username ?? '');
      setPassword('');
      setTestError(null);
    }
  }, [open, library]);

  if (!library) return null;

  const primary = isPrimaryLibrary(library.id);

  const handleSave = async () => {
    if (!username || !password) return;
    setTesting(true);
    setTestError(null);
    try {
      // Round-trip the new credentials through the proxy before saving
      // so the user sees a clean error if creds are wrong, instead of
      // saving bad creds and getting stuck looping through 401 retries
      // on the next browse.
      const params = new URLSearchParams({
        url: library.rootUrl,
        username,
        password,
      });
      const res = await fetch(`/api/opds/feed?${params}`);
      if (res.status === 401 || res.status === 403) {
        setTestError('Invalid username or password.');
        setTesting(false);
        return;
      }
      if (!res.ok) {
        setTestError(`Server error: ${res.status}`);
        setTesting(false);
        return;
      }
      updateLibrary(library.id, {
        auth: { type: 'basic', username, password },
      });
      setTesting(false);
      onSaved?.();
      onClose();
    } catch (e) {
      setTestError(e instanceof Error ? e.message : 'Connection failed');
      setTesting(false);
    }
  };

  const handleClear = () => {
    if (!library.auth) return;
    updateLibrary(library.id, { auth: undefined });
    onSaved?.();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={
        library.auth
          ? `Update credentials for ${library.name}`
          : `Sign in to ${library.name}`
      }
    >
      <div className="space-y-4">
        {/* URL surfaced read-only so the user can verify they're authing
            against the expected server. For the primary library this
            is the only way to see the URL anywhere in the UI. */}
        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">
            Library URL
          </label>
          <div
            className={
              'w-full rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm text-neutral-600 ' +
              (primary ? 'cursor-not-allowed' : '')
            }
            title={primary ? 'Primary library URL is fixed' : undefined}
          >
            {library.rootUrl}
          </div>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">
            Username
          </label>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">
            Password
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {testError && (
          <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {testError}
          </div>
        )}

        <div className="flex items-center justify-between gap-2 pt-2">
          {library.auth && !primary ? (
            <button
              type="button"
              onClick={handleClear}
              className="rounded-lg border border-neutral-300 px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
            >
              Clear credentials
            </button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!username || !password || testing}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {testing ? 'Verifying…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
